import { useState, useEffect, useMemo } from 'react';
import { Col, Container, Row, Nav, Tab, TabContent } from 'react-bootstrap';
import { useLoaderData } from 'react-router-dom';
import { PaymentReport } from './Reports/PaymentReport';
import { PayoutSummaryReport } from './Reports/PayoutSummaryReport';
import type { ReportFacilityOption } from './Reports/ReportFacilityMultiSelect';
import { RecurringReports } from './Reports/RecurringReports';
import { ServerTransferSavedReportResponse, FacilityApi, ServerTransferFacilityLite, ServerTransferRecurringReportScheduleResponse } from './shared/api/axios-client';
import { createApiService } from './shared/apiConfig';
import { ArrowRepeat } from 'react-bootstrap-icons';
import './ReportsPage.css';
import { CustomReport } from './Reports/CustomReport';

export function OperatorReportsPage() {
    const [operatorId, initialPaymentReports, initialPayoutSummaryReports, recurringReports, hasCustomReports, initialCustomSavedReports] = useLoaderData() as [
        number,
        ServerTransferSavedReportResponse[],
        ServerTransferSavedReportResponse[],
        ServerTransferRecurringReportScheduleResponse[],
        boolean,
        ServerTransferSavedReportResponse[],
    ];
    const [paymentSavedReports, setPaymentSavedReports] = useState(initialPaymentReports);
    const [payoutSummarySavedReports, setPayoutSummarySavedReports] = useState(initialPayoutSummaryReports);
    const [customSavedReports, setCustomSavedReports] = useState(initialCustomSavedReports);
    const [facilities, setFacilities] = useState<ReportFacilityOption[]>([]);
    const [selectedFacilityIds, setSelectedFacilityIds] = useState<number[]>([]);
    const [activeTab, setActiveTab] = useState('revenue');

    // Load facilities for multi-select; staff choose which facilities to include (none selected by default).
    useEffect(() => {
        const loadFacilities = async () => {
            const facilityService = createApiService(FacilityApi);
            const response = await facilityService.listFacilitiesLite({});
            if (response.data.items) {
                const validFacilities: ReportFacilityOption[] = response.data.items
                    .filter((f): f is ServerTransferFacilityLite & { facilityId: number } => f.facilityId != null)
                    .map(({ facilityId, name }) => ({ facilityId, name }));
                setFacilities(validFacilities);
            }
        };
        loadFacilities();
    }, []);

    const allSavedReports = useMemo(
        () => [
            ...paymentSavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
            ...payoutSummarySavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
            ...customSavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
        ],
        [paymentSavedReports, payoutSummarySavedReports, customSavedReports]
    );

    return (
        <Container fluid className="h-100 d-flex flex-column overflow-hidden">
            <Row className="flex-grow-1">
                <Col className="h-100">
                    <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'revenue')} className="mb-3 d-flex">
                        <Nav.Item>
                            <Nav.Link eventKey="revenue">Revenue</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="payoutsummary">Payout Summary</Nav.Link>
                        </Nav.Item>
                        {hasCustomReports && (
                            <Nav.Item>
                                <Nav.Link eventKey="custom">Custom Reports</Nav.Link>
                            </Nav.Item>
                        )}
                        <Nav.Item className="ms-auto">
                            <Nav.Link eventKey="recurring">
                                <ArrowRepeat className="me-1 icon-shift-up" />
                                Recurring Reports
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                    
                    <TabContent>
                        <Tab.Pane eventKey="revenue" active={activeTab === 'revenue'}>
                            <PaymentReport
                                facilityIds={selectedFacilityIds}
                                facilities={facilities}
                                savedReports={paymentSavedReports}
                                setSavedReports={setPaymentSavedReports}
                                heightOffset={120}
                            />
                        </Tab.Pane>
                        <Tab.Pane eventKey="payoutsummary" active={activeTab === 'payoutsummary'}>
                            <PayoutSummaryReport
                                operatorId={operatorId}
                                savedReports={payoutSummarySavedReports}
                                setSavedReports={setPayoutSummarySavedReports}
                                heightOffset={-50}
                            />
                        </Tab.Pane>
                        {hasCustomReports && (
                            <Tab.Pane eventKey="custom" active={activeTab === 'custom'}>
                                <CustomReport
                                    facilities={facilities}
                                    facilityIds={selectedFacilityIds}
                                    savedReports={customSavedReports}
                                    setSavedReports={setCustomSavedReports}
                                />
                            </Tab.Pane>
                        )}
                        <Tab.Pane eventKey="recurring" active={activeTab === 'recurring'}>
                            <RecurringReports
                                savedReports={allSavedReports}
                                recurringReports={recurringReports}
                                onReportRecurringCreated={(savedReportId) => {
                                    setPaymentSavedReports((prev) =>
                                        prev.map((r) =>
                                            r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                        )
                                    );
                                    setCustomSavedReports((prev) =>
                                        prev.map((r) =>
                                            r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                        )
                                    );
                                    setPayoutSummarySavedReports((prev) =>
                                        prev.map((r) =>
                                            r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                        )
                                    );
                                }}
                            />
                        </Tab.Pane>
                    </TabContent>
                </Col>
            </Row>
        </Container>
    );
} 